import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { SubscriberComms } from '../components/SubscriberComms'
import { PageHeader, Panel, DataTable, Chip, Button, Modal, Field, FormRow, Textarea, Select, Checkbox, Banner, StatTile, useToast, type Column } from '../ds/index'
import { RateBar } from '../components/Viz'
import { useApi } from '../lib/useApi'
import { post, patch } from '../lib/api'
import { money, num, title } from '../lib/format'
import type { Subscriber, Category, Area, User, Ref, Contact, ContactRole } from '../lib/types'
import { CONTACT_ROLES } from '../lib/types'
import { LoadingBlock, ErrorBlock } from '../components/Loading'

type BalanceLite = { id: string; credit_cents: number; open_disputes: number; logins: number }

/** Every client company, one row each; a row opens that client's page. */
export default function Subscribers() {
  const subs = useApi<{ subscribers: Subscriber[] }>('/api/subscribers')
  const bal = useApi<{ clients: BalanceLite[] }>('/api/clients/balances')
  const navigate = useNavigate()
  const [editing, setEditing] = useState<Subscriber | 'new' | null>(null)
  const [inviting, setInviting] = useState(false)
  const balOf = (id: string) => bal.data?.clients.find((c) => c.id === id)
  const openDisputes = (bal.data?.clients || []).filter((c) => c.open_disputes > 0)
  const active = (subs.data?.subscribers || []).filter((s) => s.status === 'active')
  const billed30 = (subs.data?.subscribers || []).reduce((a, s) => a + (s.billed_30d_cents || 0), 0)
  const unpriced = active.filter((s) => s.price_per_lead_cents == null).length

  const names = (xs: Ref[]) => xs.map((x) => x.name).join(', ')
  const cols: Column<Subscriber>[] = [
    { key: 'name', header: 'Client', cell: (r) => <><strong className="nowrap">{r.name}</strong><div className="muted">{r.category_name}{r.areas.length ? ` in ${names(r.areas)}` : ''}</div></>, sortBy: (r) => r.name },
    { key: 'placement', header: 'Rep and groups', cell: (r) => <><div className="nowrap">{r.reps.length ? names(r.reps) : <Chip tone="warn">no rep</Chip>}</div><div className="muted nowrap">{r.groups.length}{r.groups_included ? ` of ${r.groups_included}` : ''} groups{r.plan_name ? `, ${r.plan_name.split(',')[0]}` : ''}</div></>, sortBy: (r) => r.groups.length },
    { key: 'leads', header: 'Leads, 30 days', numeric: true, cell: (r) => num(r.leads_30d), sortBy: (r) => r.leads_30d },
    { key: 'replied', header: 'Replied', cell: (r) => <RateBar num={r.responded_30d} den={r.leads_30d} />, sortBy: (r) => r.leads_30d ? r.responded_30d / r.leads_30d : -1 },
    { key: 'price', header: 'Per lead', numeric: true, cell: (r) => r.price_per_lead_cents != null ? money(r.price_per_lead_cents) : <Chip tone="warn">no price</Chip>, sortBy: (r) => r.price_per_lead_cents ?? -1 },
    { key: 'billed', header: 'Billed, 30 days', numeric: true, cell: (r) => r.billed_30d_cents ? money(r.billed_30d_cents) : <span className="muted">none</span>, sortBy: (r) => r.billed_30d_cents || 0 },
    { key: 'credit', header: 'Lead credit', numeric: true, cell: (r) => { const b = balOf(r.id); return b ? money(b.credit_cents) : <span className="muted">-</span> }, sortBy: (r) => balOf(r.id)?.credit_cents ?? 0 },
    { key: 'disputes', header: 'Disputes', cell: (r) => { const n = balOf(r.id)?.open_disputes || 0; return n ? <Chip tone="warn" dot>{n} open</Chip> : null }, sortBy: (r) => balOf(r.id)?.open_disputes ?? 0 },
    { key: 'status', header: 'Status', cell: (r) => <Chip tone={r.status === 'active' ? undefined : 'warn'} dot={r.status !== 'active'}>{title(r.status)}</Chip>, sortBy: (r) => r.status },
  ]

  return (
    <>
      <PageHeader title="Clients" subtitle={`The businesses reps recommend. Open one for its details, balance, disputes and portal. ${active.length} active, ${money(billed30)} of leads delivered in the last 30 days${unpriced ? `, ${unpriced} with no price per lead` : ''}.`} actions={<Button onClick={() => setInviting(true)}>Add client</Button>} />
      {openDisputes.length > 0 && (
        <div style={{ marginBottom: 'var(--s-6)' }}>
          <Banner tone="warn" title={`${openDisputes.reduce((a, c) => a + c.open_disputes, 0)} open dispute${openDisputes.reduce((a, c) => a + c.open_disputes, 0) === 1 ? '' : 's'} to decide`}>
            {openDisputes.map((c, i) => <span key={c.id}>{i ? ', ' : ''}<a href={`/clients/${c.id}#disputes`} onClick={(e) => { e.preventDefault(); navigate(`/clients/${c.id}#disputes`) }}>{(subs.data?.subscribers || []).find((s) => s.id === c.id)?.name || 'a client'}</a></span>)}
          </Banner>
        </div>
      )}
      {subs.data && (() => {
        const act = subs.data.subscribers.filter((s) => s.status === 'active')
        const leads = act.reduce((a, s) => a + s.leads_30d, 0)
        const replied = act.reduce((a, s) => a + s.responded_30d, 0)
        return (
          <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
            <StatTile focal label="Active clients" value={act.length} meta={`${subs.data.subscribers.length - act.length} paused or cancelled`} />
            <StatTile label="Billed, 30 days" value={money(billed30)} meta={replied ? `${money(Math.round(billed30 / replied))} a delivered lead` : 'no leads delivered'} />
            <StatTile label="Leads sent, 30 days" value={leads} meta={`${replied} replied`} progress={leads ? (replied / leads) * 100 : undefined} />
            <StatTile label="Without a rep or a group" value={act.filter((s) => !s.reps.length || !s.groups.length).length} meta="these get no leads" />
          </div>
        )
      })()}
      {subs.error && <ErrorBlock error={subs.error} />}
      <Panel className="panel-flush">
        {!subs.data ? <div style={{ padding: 'var(--s-6)' }}><LoadingBlock /></div> : <DataTable columns={cols} rows={subs.data.subscribers} rowKey={(r) => r.id} onRowClick={(r) => navigate(`/clients/${r.id}`)} />}
      </Panel>
      {inviting && <NewClient onClose={() => setInviting(false)} onCreated={() => subs.reload()} onFullForm={() => { setInviting(false); setEditing('new') }} />}
      {editing && <SubscriberForm sub={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); subs.reload() }} />}
    </>
  )
}

function Picker({ label, items, value, onChange, help }: { label: string; items: Ref[]; value: string[]; onChange: (v: string[]) => void; help?: string }) {
  return (
    <FormRow label={`${label} (${value.length})`} help={help}>
      <div className="picker">
        {items.map((it) => (
          <Checkbox key={it.id} label={it.name} checked={value.includes(it.id)}
                    onChange={(e) => onChange(e.target.checked ? [...value, it.id] : value.filter((x) => x !== it.id))} />
        ))}
      </div>
    </FormRow>
  )
}

export function SubscriberForm({ sub, onClose, onSaved }: { sub: Subscriber | null; onClose: () => void; onSaved: () => void }) {
  const cats = useApi<{ categories: Category[] }>('/api/categories')
  const areas = useApi<{ areas: Area[] }>('/api/areas')
  const users = useApi<{ users: User[] }>('/api/users')
  const toast = useToast()
  const [f, setF] = useState({
    name: sub?.name || '', category_id: sub?.category_id || '', website: sub?.website || '', pitch: sub?.pitch || '', plan_name: sub?.plan_name || '',
    about: sub?.about || '', services: sub?.services || '', good_fit: sub?.good_fit || '', not_a_fit: sub?.not_a_fit || '',
    monthly: sub?.monthly_price_cents != null ? String(sub.monthly_price_cents / 100) : '', groups_included: sub?.groups_included != null ? String(sub.groups_included) : '',
    per_lead: sub?.price_per_lead_cents != null ? String(sub.price_per_lead_cents / 100) : '',
    involvement: sub?.involvement || 'leads', status: sub?.status || 'active', email_mode: sub?.email_mode || 'each',
    started_on: sub?.started_on ? sub.started_on.slice(0, 10) : '', ended_on: sub?.ended_on ? sub.ended_on.slice(0, 10) : '',
    new_category: '',
  })
  const [contacts, setContacts] = useState<Contact[]>(sub?.contacts?.length ? sub.contacts : [{ name: '', role: 'owner', role_other: null, email: '', phone: '', is_primary: true, lead_emails: true, billing_emails: true }])
  const [areaIds, setAreaIds] = useState<string[]>(sub?.areas.map((a) => a.id) || [])
  const [repIds, setRepIds] = useState<string[]>(sub?.reps.map((r) => r.id) || [])
  const [err, setErr] = useState<string | null>(null)
  const set = (k: string, v: string) => setF((s) => ({ ...s, [k]: v }))
  const setContact = (i: number, patchC: Partial<Contact>) => setContacts((cs) => cs.map((c, j) => (j === i ? { ...c, ...patchC } : patchC.is_primary ? { ...c, is_primary: false } : c)))
  const addContact = () => setContacts((cs) => [...cs, { name: '', role: 'other', role_other: null, email: '', phone: '', is_primary: cs.length === 0, lead_emails: false, billing_emails: false }])
  const removeContact = (i: number) => setContacts((cs) => {
    const next = cs.filter((_, j) => j !== i)
    if (next.length && !next.some((c) => c.is_primary)) next[0] = { ...next[0], is_primary: true }
    return next
  })

  const save = async () => {
    setErr(null)
    const kept = contacts.filter((c) => c.name.trim() || c.email?.trim() || c.phone?.trim())
    const body = {
      ...f, category_id: f.category_id === OTHER ? null : f.category_id, new_category: f.category_id === OTHER ? f.new_category.trim() : '',
      monthly_price_cents: f.monthly ? Math.round(Number(f.monthly) * 100) : null, groups_included: f.groups_included ? Number(f.groups_included) : null,
      price_per_lead_cents: f.per_lead ? Math.round(Number(f.per_lead) * 100) : null,
      contacts: kept.map((c) => ({ ...c, email: c.email?.trim() || null, phone: c.phone?.trim() || null })),
      area_ids: areaIds, rep_ids: repIds,
      started_on: f.started_on || null, ended_on: f.status === 'cancelled' ? (f.ended_on || null) : null,
    }
    try {
      const r = sub ? await patch<{ warnings: string[] }>(`/api/subscribers/${sub.id}`, body) : await post<{ warnings: string[] }>('/api/subscribers', body)
      if (r.warnings.length) toast(`Saved. Overlap: ${r.warnings.join('; ')}`, 'warn')
      else toast('Saved', 'pos')
      onSaved()
    } catch (e) { setErr((e as Error).message) }
  }

  const reps = (users.data?.users || []).filter((u) => u.role === 'rep')
  const chosenReps = reps.filter((r) => repIds.includes(r.id))
  const repGroups = [...new Set(chosenReps.flatMap((r) => r.group_names))].sort()

  return (
    <div className="wide-modal">
      <Modal title={sub ? sub.name : 'Add client'} onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!f.name.trim() || !f.category_id || (f.category_id === OTHER && !f.new_category.trim())}>Save</Button></>}>
        <div className="stack-tight">
          <div className="s-label muted">Business</div>
          <div className="two-col" style={{ marginBottom: 0 }}>
            <Field label="Business name" value={f.name} onChange={(e) => set('name', e.target.value)} />
            <CategoryPicker categories={cats.data?.categories || []} value={f.category_id} typed={f.new_category} onChange={(id, typed) => setF((s) => ({ ...s, category_id: id, new_category: typed }))} />
            <Field label="Website" value={f.website} onChange={(e) => set('website', e.target.value)} />
            <FormRow label="Status"><Select value={f.status} onChange={(e) => set('status', e.target.value)}><option value="active">Active</option><option value="paused">Paused</option><option value="cancelled">Cancelled</option></Select></FormRow>
            <Field label="Signed on" type="date" value={f.started_on} onChange={(e) => set('started_on', e.target.value)} />
            {f.status === 'cancelled' && <Field label="Cancelled on (today if blank)" type="date" value={f.ended_on} onChange={(e) => set('ended_on', e.target.value)} />}
          </div>

          <div className="s-label muted" style={{ marginTop: 'var(--s-4)' }}>Contacts</div>
          <p className="form-help" style={{ margin: 0 }}>Everyone you deal with at {f.name.trim() || 'this business'}. The primary contact is who you call first. Tick who should get lead emails (each recommendation and the monthly summary) and billing emails.</p>
          <div className="contact-list">
            {contacts.map((c, i) => (
              <div key={c.id || `new-${i}`} className="contact-card">
                <div className="contact-grid">
                  <Field id={`contact-${i}-name`} label="Name" value={c.name} onChange={(e) => setContact(i, { name: e.target.value })} />
                  <FormRow label="Role" htmlFor={`contact-${i}-role`}><Select id={`contact-${i}-role`} value={c.role} onChange={(e) => setContact(i, { role: e.target.value as ContactRole })}>{CONTACT_ROLES.map(([k, n]) => <option key={k} value={k}>{n}</option>)}</Select></FormRow>
                  {c.role === 'other' && <Field id={`contact-${i}-role-other`} label="Their role" value={c.role_other || ''} onChange={(e) => setContact(i, { role_other: e.target.value })} placeholder="Dispatcher, office lead..." />}
                  <Field id={`contact-${i}-email`} label="Email" type="email" value={c.email || ''} onChange={(e) => setContact(i, { email: e.target.value })} />
                  <Field id={`contact-${i}-phone`} label="Phone" type="tel" value={c.phone || ''} onChange={(e) => setContact(i, { phone: e.target.value })} />
                </div>
                <div className="row-tight contact-flags">
                  <Checkbox label="Primary contact" checked={c.is_primary} onChange={(e) => e.target.checked && setContact(i, { is_primary: true })} />
                  <Checkbox label="Lead emails" checked={c.lead_emails} onChange={(e) => setContact(i, { lead_emails: e.target.checked })} />
                  <Checkbox label="Billing emails" checked={c.billing_emails} onChange={(e) => setContact(i, { billing_emails: e.target.checked })} />
                  <Button variant="ghost" onClick={() => removeContact(i)} aria-label={`Remove ${c.name || 'this contact'}`}>Remove</Button>
                </div>
              </div>
            ))}
            <div><Button variant="secondary" onClick={addContact}>Add contact</Button></div>
          </div>
          <FormRow label="Lead emails" help="How often the ticked contacts hear about their recommendations. Their portal shows every recommendation either way.">
            <Select value={f.email_mode} onChange={(e) => set('email_mode', e.target.value)}>
              <option value="each">Each recommendation, plus a monthly summary</option>
              <option value="monthly">Monthly summary only</option>
              <option value="off">No emails</option>
            </Select>
          </FormRow>

          <div className="s-label muted" style={{ marginTop: 'var(--s-4)' }}>Pricing and plan</div>
          <div className="two-col" style={{ marginBottom: 0 }}>
            <Field label="Price per lead, $" type="number" step="0.01" min={0} value={f.per_lead} onChange={(e) => set('per_lead', e.target.value)} />
            <FormRow label="Involvement"><Select value={f.involvement} onChange={(e) => set('involvement', e.target.value)}><option value="leads">Leads only</option><option value="calls">We call and schedule</option><option value="sales">We sell</option></Select></FormRow>
            <Field label="Plan (before per-lead billing)" value={f.plan_name} onChange={(e) => set('plan_name', e.target.value)} />
            <Field label="Old monthly price, $" type="number" value={f.monthly} onChange={(e) => set('monthly', e.target.value)} />
          </div>

          <div className="s-label muted" style={{ marginTop: 'var(--s-4)' }}>Who covers them</div>
          <Picker label="Rep who represents them" items={reps} value={repIds} onChange={setRepIds} help="Usually one. They get leads from every group their rep works." />
          <p className="form-help" style={{ margin: 0 }}>
            {chosenReps.length === 0 ? 'Choose a rep: a client with no rep gets no leads.'
              : repGroups.length ? `Gets leads from ${chosenReps.map((r) => r.name.split(' ')[0]).join(' and ')}'s ${repGroups.length} group${repGroups.length === 1 ? '' : 's'}: ${repGroups.join(', ')}.`
              : `${chosenReps.map((r) => r.name.split(' ')[0]).join(' and ')} is not in any group yet, so no leads until they join some.`}
          </p>
          <Picker label="Areas they cover" items={areas.data?.areas.filter((a) => a.active) || []} value={areaIds} onChange={setAreaIds} />

          <div className="s-label muted" style={{ marginTop: 'var(--s-4)' }}>Profile for reps</div>
          <p className="form-help" style={{ margin: 0 }}>Reps read this under Clients before recommending them. Leave out anything a rep should not repeat.</p>
          <FormRow label="What the rep says about them"><Textarea rows={2} value={f.pitch} onChange={(e) => set('pitch', e.target.value)} /></FormRow>
          <FormRow label="About the business"><Textarea aria-label="About the business" rows={3} value={f.about} onChange={(e) => set('about', e.target.value)} placeholder="How long they have been around, who runs it, what they are known for" /></FormRow>
          <FormRow label="Services they offer"><Textarea aria-label="Services they offer" rows={2} value={f.services} onChange={(e) => set('services', e.target.value)} /></FormRow>
          <div className="two-col" style={{ marginBottom: 0 }}>
            <FormRow label="A good fit for"><Textarea aria-label="A good fit for" rows={2} value={f.good_fit} onChange={(e) => set('good_fit', e.target.value)} placeholder="The jobs they want" /></FormRow>
            <FormRow label="Jobs they do not take"><Textarea aria-label="Jobs they do not take" rows={2} value={f.not_a_fit} onChange={(e) => set('not_a_fit', e.target.value)} placeholder="Jobs to send elsewhere" /></FormRow>
          </div>
          {sub && <SubscriberComms sub={sub} />}
          {err && <p className="form-error">{err}</p>}
        </div>
      </Modal>
    </div>
  )
}

/**
 * Add a client the quick way: their name, category and one contact. The
 * platform makes the client and a login for that contact, and gives back a
 * one-time link they use to set a password and fill in their own business
 * profile in the portal.
 */
export function NewClient({ onClose, onCreated, onFullForm, forRep = false }: { onClose: () => void; onCreated: () => void; onFullForm?: () => void; forRep?: boolean }) {
  const cats = useApi<{ categories: Category[] }>('/api/categories')
  const navigate = useNavigate()
  const toast = useToast()
  const [f, setF] = useState({ name: '', category_id: '', new_category: '', contact_name: '', email: '' })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [made, setMade] = useState<{ client: { id: string; name: string }; login: { id: string; name: string; email: string }; url: string } | null>(null)
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }))

  const form = useRef<HTMLFormElement>(null)
  const create = async () => {
    // What is in the boxes, not only what React heard: Chrome's autofill can show
    // values the page was never told about, which left this button dead.
    const fd = form.current ? new FormData(form.current) : null
    const v = (k: 'name' | 'new_category' | 'contact_name' | 'email') => (f[k] || String(fd?.get(k) || '')).trim()
    const now = { ...f, name: v('name'), new_category: v('new_category'), contact_name: v('contact_name'), email: v('email') }
    const missing = [!now.name && 'business name', !now.category_id && 'category', now.category_id === OTHER && !now.new_category && 'the category name',
      !now.contact_name && "contact's name", !now.email && "contact's email"].filter(Boolean)
    if (missing.length) return setErr(`Fill in the ${missing.join(', ')}.`)
    setF(now); setBusy(true); setErr(null)
    try {
      const r = await post<{ client: { id: string; name: string }; login: { id: string; name: string; email: string }; invite: { url: string } }>('/api/clients/invite',
        { ...now, category_id: now.category_id === OTHER ? null : now.category_id, new_category: now.category_id === OTHER ? now.new_category : '' })
      setMade({ client: r.client, login: r.login, url: r.invite.url }); onCreated()
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }
  const copy = () => navigator.clipboard?.writeText(made?.url || '').then(() => toast('Link copied', 'pos'), () => toast('Select the link and copy it', 'neg'))
  const emailIt = async () => {
    if (!made) return
    setBusy(true)
    try {
      // A fresh link, emailed; the one shown before stops working.
      const r = await post<{ url: string; emailed: boolean; email_error: string | null }>(`/api/users/${made.login.id}/invite`, { send_email: true })
      setMade({ ...made, url: r.url })
      if (r.emailed) toast(`Emailed to ${made.login.email}`, 'pos'); else toast(r.email_error || 'Not emailed', 'warn')
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }

  if (made) {
    return (
      <Modal title={`${made.client.name} is added`} onClose={onClose}
             footer={<><Button variant="secondary" onClick={onClose}>Done</Button>{!forRep && <Button onClick={() => navigate(`/clients/${made.client.id}`)}>Open their page</Button>}</>}>
        <div className="stack-tight">
          <p style={{ margin: 0 }}>Send this link to {made.login.name.split(' ')[0]}. It opens a page to choose a password, then a short welcome that walks them through their business profile (where they work, what they do, the leads they want) and their monthly lead credit. It works once, for 7 days.</p>
          <code className="comms-url invite-url">{made.url}</code>
          <div className="row-tight" style={{ flexWrap: 'wrap' }}>
            <Button onClick={copy}>Copy link</Button>
            <Button variant="secondary" onClick={emailIt} disabled={busy}>{busy ? 'Emailing' : `Email it to ${made.login.email}`}</Button>
          </div>
          <p className="form-help" style={{ margin: 0 }}>{forRep ? 'They are on your list now. Your admin sets their pricing and areas.' : 'Assign their rep and areas on their page, so leads can reach them.'}</p>
        </div>
      </Modal>
    )
  }
  return (
    <Modal title="Add client" onClose={onClose}
           footer={<>{onFullForm && <Button variant="ghost" onClick={onFullForm}>Fill in everything myself</Button>}<Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={create} disabled={busy}>{busy ? 'Adding' : 'Add and get their link'}</Button></>}>
      <form ref={form} className="stack-tight" onSubmit={(e) => { e.preventDefault(); create() }}>
        <p className="form-help" style={{ margin: 0 }}>Just the basics. They fill in the rest of their company information themselves from the link.</p>
        <Field label="Business name" name="name" value={f.name} onChange={(e) => set('name', e.target.value)} autoFocus />
        <CategoryPicker categories={cats.data?.categories || []} value={f.category_id} typed={f.new_category} help="Which leads they buy." onChange={(id, typed) => setF((s) => ({ ...s, category_id: id, new_category: typed }))} />
        <Field label="Contact's name" name="contact_name" value={f.contact_name} onChange={(e) => set('contact_name', e.target.value)} />
        <Field label="Contact's email" name="email" type="email" value={f.email} onChange={(e) => set('email', e.target.value)} />
        {err && <p className="form-error">{err}</p>}
        <button type="submit" hidden />
      </form>
    </Modal>
  )
}

const OTHER = '__other'

/** The category list plus "Other", which asks for the name of a new category (made when the client is saved). */
function CategoryPicker({ categories, value, typed, onChange, help }: { categories: Category[]; value: string; typed: string; onChange: (id: string, typed: string) => void; help?: string }) {
  return (
    <>
      <FormRow label="Category" help={value === OTHER ? undefined : help}>
        <Select value={value} onChange={(e) => onChange(e.target.value, e.target.value === OTHER ? typed : '')}>
          <option value="">Choose</option>
          {categories.filter((c) => c.active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          <option value={OTHER}>Other (type it)</option>
        </Select>
      </FormRow>
      {value === OTHER && (
        <Field label="What category?" name="new_category" value={typed} onChange={(e) => onChange(OTHER, e.target.value)} placeholder="Pest control, pool service..." autoFocus />
      )}
    </>
  )
}
